import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { api } from '@/shared/api/axios';
import type { Task, TasksFilters, CreateTaskPayload, UpdateTaskPayload, ProjectOption } from '../types';

interface TasksResponse {
  tasks: Task[];
}

// ─── Queries ────────────────────────────────────────────────────────────────

export function useTasks(filters: TasksFilters = {}) {
  return useQuery({
    queryKey: ['tasks', filters],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (filters.projectId) params.set('projectId', filters.projectId);
      if (filters.status) params.set('status', filters.status);
      if (filters.dept) params.set('dept', filters.dept);
      if (filters.assignedTo) params.set('assignedTo', filters.assignedTo);
      const qs = params.toString();
      const { data } = await api.get<TasksResponse>(`/api/tasks${qs ? `?${qs}` : ''}`);
      return data.tasks ?? [];
    },
    placeholderData: keepPreviousData,
  });
}

export function useTaskDetail(id: string | null) {
  return useQuery({
    queryKey: ['tasks', 'detail', id],
    queryFn: async () => {
      const { data } = await api.get<{ task: Task }>(`/api/tasks/${id}`);
      return data.task;
    },
    enabled: !!id,
  });
}

export function useProjectOptions() {
  return useQuery({
    queryKey: ['projects', 'options'],
    queryFn: async () => {
      const { data } = await api.get<{ projects: ProjectOption[] }>('/api/projects');
      return data.projects ?? [];
    },
    staleTime: 5 * 60 * 1000, // 5 min – dropdown data is stable
  });
}

// ─── Mutations ───────────────────────────────────────────────────────────────

export function useTaskMutations() {
  const queryClient = useQueryClient();

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['tasks'] });
  };

  const createTask = useMutation({
    mutationFn: async (payload: CreateTaskPayload) => {
      const { data } = await api.post<{ ok: boolean; id: string }>('/api/tasks', payload);
      return data;
    },
    onSuccess: invalidate,
  });

  const updateTask = useMutation({
    mutationFn: async ({ id, payload }: { id: string; payload: UpdateTaskPayload }) => {
      const { data } = await api.patch<{ ok: boolean }>(`/api/tasks/${id}`, payload);
      return data;
    },
    onSuccess: invalidate,
  });

  const deleteTask = useMutation({
    mutationFn: async (id: string) => {
      const { data } = await api.delete<{ ok: boolean }>(`/api/tasks/${id}`);
      return data;
    },
    onSuccess: invalidate,
  });

  return { createTask, updateTask, deleteTask };
}
